import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Phone, Mail } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHead, CtaBand, Breadcrumbs } from "@/components/Shared";
import { INSIGHTS, BRAND } from "@/data/site";
import { url, metaInsights, metaArticle, articleBySlug, breadcrumbLd, articleLd } from "@/data/content";
import NotFound from "@/pages/NotFound";

export function InsightsIndex() {
  const meta = metaInsights();
  return (
    <div data-testid="insights-index-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
      <Seo title={meta.title} description={meta.description} path="/insights/" jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights/" }])]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Insights", path: "/insights/" }]} />
      <SectionHead as="h1" eyebrow="Security Insights Canada" title="Guides that make you a smarter security buyer" desc="Licensing rules, loss prevention tactics, fire watch compliance and event planning — written by operators, not marketers." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {INSIGHTS.map((a) => (
          <Link key={a.slug} to={url.article(a)} data-testid="seo-insights-card" className="group rounded-2xl border border-slate-800 bg-[#111827] p-7 transition-all hover:border-amber-500/40 hover:-translate-y-1">
            <div className="flex items-center gap-2 mb-4">
              {a.tags.map((t) => (
                <span key={t} className="rounded-full bg-amber-500/10 border border-amber-500/30 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-amber-400">{t}</span>
              ))}
            </div>
            <h2 className="font-display font-bold text-slate-100 leading-snug group-hover:text-amber-400 transition-colors mb-3">{a.title}</h2>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">{a.excerpt}</p>
            <span className="text-xs font-mono text-slate-500 flex items-center gap-2"><Clock className="h-3 w-3" /> {a.minutes} min read</span>
          </Link>
        ))}
      </div>
      <CtaBand heading="Questions a Guide Didn't Answer?" sub={`Call ${BRAND.phone} or email ${BRAND.email} — our operations team answers security questions all day, every day.`} />
    </div>
  );
}

export function ArticlePage() {
  const { articleSlug } = useParams();
  const a = articleBySlug(articleSlug);
  if (!a) return <NotFound />;
  const meta = metaArticle(a);
  const others = INSIGHTS.filter((x) => x.slug !== a.slug).slice(0, 3);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Insights", path: "/insights/" }, { name: a.title, path: url.article(a) }];
  return (
    <div data-testid={`article-page-${a.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.article(a)} jsonLd={[articleLd(a), breadcrumbLd(crumbs)]} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <Breadcrumbs items={crumbs} />
        <div className="flex items-center gap-2 mb-5">
          {a.tags.map((t) => (
            <span key={t} className="rounded-full bg-amber-500/10 border border-amber-500/30 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-amber-400">{t}</span>
          ))}
        </div>
        <h1 data-testid="article-title" className="text-3xl sm:text-4xl font-extrabold font-display tracking-tight text-slate-50 leading-tight">{a.title}</h1>
        <p className="mt-4 flex items-center gap-4 text-xs font-mono text-slate-500">
          <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {a.date}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> {a.minutes} min read</span>
        </p>
        <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed border-l-2 border-amber-500 pl-5">{a.excerpt}</p>
        <div className="mt-10 space-y-10">
          {a.body.map((sec, i) => (
            <section key={i}>
              <h2 className="text-xl sm:text-2xl font-bold font-display text-slate-50 tracking-tight mb-4">{sec.h}</h2>
              {sec.ps.map((p, j) => (
                <p key={j} className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">{p}</p>
              ))}
            </section>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-amber-500/25 bg-[#111827] p-7">
          <h2 className="font-display font-bold text-slate-50 mb-2">Need professional security guards in Canada?</h2>
          <p className="text-sm text-slate-400 mb-5">{BRAND.name} deploys licensed guards in every province and 55+ cities, 24/7/365.</p>
          <div className="flex flex-wrap gap-3">
            <a href={BRAND.phoneHref} data-testid="article-call-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="article-email-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
              <Mail className="h-4 w-4" /> {BRAND.email}
            </a>
          </div>
        </div>
      </article>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-10">
        <h2 className="text-xl font-bold font-display text-slate-50 mb-6">Keep Reading</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {others.map((o) => (
            <Link key={o.slug} to={url.article(o)} className="group rounded-xl border border-slate-800 bg-[#111827] p-6 transition-all hover:border-amber-500/40">
              <h3 className="font-display font-bold text-sm text-slate-100 group-hover:text-amber-400 transition-colors leading-snug mb-3">{o.title}</h3>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-500">Read guide <ArrowRight className="h-3 w-3" /></span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
